// Fetch-based HttpClient implementation for browser and Node.js environments.
import type { HttpClient, HttpResponse } from './types.js';

export class FetchHttpClient implements HttpClient {
  private readonly timeoutMs: number;

  constructor(timeoutMs = 30_000) {
    this.timeoutMs = timeoutMs;
  }

  async post(
    url: string,
    headers: Array<[string, string]>,
    body: Uint8Array
  ): Promise<HttpResponse> {
    return this.request('POST', url, headers, body);
  }

  async get(url: string, headers: Array<[string, string]>): Promise<HttpResponse> {
    return this.request('GET', url, headers);
  }

  private async request(
    method: string,
    url: string,
    headers: Array<[string, string]>,
    body?: Uint8Array
  ): Promise<HttpResponse> {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;

    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    if (controller) {
      timeoutHandle = setTimeout(() => {
        controller.abort();
      }, this.timeoutMs);
    }

    try {
      const headerObj: Record<string, string> = {};
      for (const [k, v] of headers) {
        headerObj[k] = v;
      }

      const resp = await fetch(url, {
        method,
        headers: headerObj,
        body: (body ?? null) as BodyInit | null,
        signal: controller?.signal ?? null
      });

      const respHeaders: Array<[string, string]> = [];
      resp.headers.forEach((value, key) => {
        respHeaders.push([key, value]);
      });

      const respBody = new Uint8Array(await resp.arrayBuffer());

      return {
        status: resp.status,
        body: respBody,
        headers: respHeaders
      };
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  }
}
